import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS, type LanguageOption } from "./flags";

export function Flag({ option }: { option: LanguageOption }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        width: 20,
        height: 14,
        flexShrink: 0,
        borderRadius: "2px",
        overflow: "hidden",
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
        "& svg": { width: "100%", height: "100%", display: "block" },
      }}
    >
      {option.flag}
    </Box>
  );
}

/** Switches the UI language and remembers it (shared by the dropdown and the compact navbar menu). */
export function changeAppLanguage(i18n: { changeLanguage: (code: string) => unknown }, code: string) {
  void i18n.changeLanguage(code);
  try {
    localStorage.setItem("app-lang", code);
  } catch {
    /* storage unavailable: language still changes for this session */
  }
}

/** Flagged language dropdown (persists to `app-lang`, same key `i18n/config.ts` reads on boot). */
export default function LanguageSelect() {
  const { t, i18n } = useTranslation();
  const resolved = i18n.resolvedLanguage ?? i18n.language ?? "tr";
  const current = LANGUAGE_OPTIONS.find((o) => resolved.startsWith(o.code)) ?? LANGUAGE_OPTIONS[0];

  const change = (option: LanguageOption | null) => {
    if (option) changeAppLanguage(i18n, option.code);
  };

  return (
    <Autocomplete
      size="small"
      disableClearable
      blurOnSelect
      selectOnFocus={false}
      options={LANGUAGE_OPTIONS}
      value={current}
      onChange={(_, option) => change(option)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      sx={{ width: { xs: 118, sm: 156 }, flexShrink: 0 }}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props} sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          <Flag option={option} />
          {option.label}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          aria-label={t("preferences.language")}
          slotProps={{
            htmlInput: { ...params.inputProps, "aria-label": t("preferences.language"), readOnly: true },
            input: {
              ...params.InputProps,
              startAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", pl: 0.5, pr: 0.25 }}>
                  <Flag option={current} />
                </Box>
              ),
            },
          }}
        />
      )}
    />
  );
}
